import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'
import { Link as RouterLink, useParams } from 'react-router'

import { getService } from '../../api/services'
import { can } from '../../auth/session'
import { usePortal } from '../../auth/sessionContext'
import { DataState } from '../../components/DataState'
import { EmptyState } from '../../components/ui/EmptyState'
import { PageHeader } from '../../components/ui/PageHeader'
import { SandboxPanel } from './SandboxPanel'
import { SANDBOX_LABEL } from './labels'

/**
 * The Sandbox for one capability, on its own route.
 *
 * Its own page rather than a panel on the service page because it is the one
 * screen in the portal that spends money, and a form that appears halfway down
 * a reference page gets submitted by somebody who was reading. A separate route
 * is also a link somebody can send to a colleague.
 *
 * The contract is fetched again here rather than passed down: this route must
 * survive a hard refresh, and a component that only works when the previous
 * page populated the cache does not.
 */
export function SandboxPage() {
  const { code = '' } = useParams()
  const { membership } = usePortal()
  const customerId = membership.customer.id
  const maySandbox = can(membership, 'run_sandbox')

  const query = useQuery({
    queryKey: ['client', 'service', customerId, code],
    queryFn: ({ signal }) => getService(customerId, code, signal),
    enabled: maySandbox,
  })

  return (
    <Box>
      <Button component={RouterLink} to={`/services/${code}`} size="small" sx={{ mb: 1 }}>
        بازگشت به جزئیات سرویس
      </Button>

      <PageHeader title={SANDBOX_LABEL.heading} />

      {maySandbox ? (
        <DataState
          isPending={query.isPending}
          isError={query.isError}
          error={query.error}
          onRetry={() => void query.refetch()}
        >
          {query.data ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {query.data.display_name_fa || query.data.name}
              </Typography>
              <SandboxPanel service={query.data} />
            </Box>
          ) : null}
        </DataState>
      ) : (
        <EmptyState
          title="اجرای آزمایشی برای نقش شما در دسترس نیست"
          hint="هر اجرا از کیف پول سازمان برداشت می‌کند، بنابراین تنها نقش‌هایی که اجازهٔ خرج کردن دارند می‌توانند آن را انجام دهند."
        />
      )}
    </Box>
  )
}
